import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AppShell3 } from '@/components/app-shell-3';
import { ToolHeader } from '@/components/ToolHeader';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { gcsUploadFile } from '@/lib/gcsStorage';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { PageTransition } from '@/components/PageTransition';
import KioskPinManager from '@/components/KioskPinManager';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  X,
  Loader2,
  Building,
  Mail,
  MapPin,
  Briefcase,
  User,
  Shield,
  Save,
  AlertCircle,
  Camera,
  Upload,
  Users,
  ChevronLeft,
  Plus,
  ShieldCheck
} from 'lucide-react';

// Navigation sections configuration
const SECTIONS = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'personal', label: 'Personal Information', icon: Mail },
  { id: 'company-role', label: 'Company Role', icon: Shield },
  { id: 'role-templates', label: 'Role Templates', icon: ShieldCheck },
  { id: 'space-access', label: 'Space Access', icon: Building },
];

// Tool configuration for ToolHeader
const GLOBAL_TOOLS = [
  { id: 'company-team', tool_type: 'team', name: 'Company Users', icon: Users, color: '#3b82f6' },
];

export function EditMemberPage() {
  const navigate = useNavigate();
  const { memberId } = useParams();
  const { company, can, loading: authLoading, permissionsLoaded } = useAuth();
  usePageTitle('Edit Team Member');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [member, setMember] = useState(null);
  const [availableProjects, setAvailableProjects] = useState([]);

  // Auth is ready when not loading AND permissions have been loaded
  const authReady = !authLoading && permissionsLoaded;

  // Redirect non-authorized users (only after auth has loaded)
  useEffect(() => {
    if (authReady && !can('users.manage')) {
      toast.error('You do not have permission to edit team members');
      navigate('/dashboard');
    }
  }, [authReady, can, navigate]);

  // Form fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [location, setLocation] = useState('');
  const [bio, setBio] = useState('');
  const [companyRole, setCompanyRole] = useState('User'); // Add company role
  const [memberSpaces, setMemberSpaces] = useState([]);

  // Avatar management
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Add space dialog
  const [addSpaceDialogOpen, setAddSpaceDialogOpen] = useState(false);

  // Role templates
  const [memberRoleTemplates, setMemberRoleTemplates] = useState([]);

  // Section navigation
  const [activeSection, setActiveSection] = useState('profile');
  const sectionRefs = useRef({});

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150; // Offset for header

      for (const section of SECTIONS) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (memberId && company?.id) {
      loadMemberData();
      loadAvailableProjects();
    }
  }, [memberId, company?.id]);

  const loadMemberData = async () => {
    setLoading(true);
    try {
      // SECURITY: First verify the member belongs to the current company
      const { data: membershipCheck, error: membershipError } = await supabase
        .from('company_members')
        .select('id')
        .eq('user_id', memberId)
        .eq('company_id', company.id)
        .single();

      if (membershipError || !membershipCheck) {
        // Member does not belong to this company - this is a security violation attempt
        console.error('Security: Attempted to access member from different company');
        toast.error('You do not have permission to edit this member');
        navigate('/company-team');
        return;
      }

      // Try to load from user_profiles first (for members with accounts)
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select(
          `
          id,
          first_name,
          last_name,
          email,
          avatar_url,
          job_title,
          location,
          bio,
          kiosk_pin
        `
        )
        .eq('id', memberId)
        .single();

      if (profileData) {
        // Member has an account
        setMember({
          ...profileData,
          hasAccount: true,
          userId: profileData.id,
          name: `${profileData.first_name || ''} ${
            profileData.last_name || ''
          }`.trim()
        });

        setFirstName(profileData.first_name || '');
        setLastName(profileData.last_name || '');
        setEmail(profileData.email || '');
        setJobTitle(profileData.job_title || '');
        setLocation(profileData.location || '');
        setBio(profileData.bio || '');

        // Load company role
        const { data: companyMemberData, error: companyError } = await supabase
          .from('company_members')
          .select('role')
          .eq('user_id', memberId)
          .eq('company_id', company.id)
          .single();

        if (companyMemberData) {
          setCompanyRole(companyMemberData.role || 'User');
        }

        // Load their space memberships
        const { data: spacesData, error: spacesError } = await supabase
          .from('space_members')
          .select(
            `
            role,
            project_id,
            projects:space_members_project_id_fkey(
              id,
              name
            )
          `
          )
          .eq('user_id', memberId);

        if (spacesData) {
          setMemberSpaces(
            spacesData.map(s => ({
              name: s.projects?.name || 'Unknown',
              role: s.role || 'Member',
              projectId: s.project_id
            }))
          );
        }

        // Load applied role templates
        const { data: templateLinks } = await supabase
          .from('user_role_templates')
          .select('role_template_id')
          .eq('user_id', memberId);

        if (templateLinks && templateLinks.length > 0) {
          const templateIds = templateLinks.map(l => l.role_template_id);
          const { data: templates } = await supabase
            .from('role_templates')
            .select('id, name, color, description, job_profile')
            .in('id', templateIds)
            .eq('is_active', true);

          setMemberRoleTemplates(templates || []);
        } else {
          setMemberRoleTemplates([]);
        }
      } else {
        // Member not found in user_profiles (shouldn't happen after migration)
        toast.error('Member not found');
        navigate('/company-team');
      }
    } catch (error) {
      console.error('Error loading member:', error);
      toast.error('Failed to load member details');
      navigate('/company-team');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableProjects = async () => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('id, name')
        .eq('company_id', company.id)
        .eq('is_archived', false)
        .order('name');

      if (error) throw error;

      setAvailableProjects(data || []);
    } catch (error) {
      console.error('Error loading projects:', error);
      toast.error('Failed to load available spaces');
    }
  };

  const handleAddSpace = async projectId => {
    const project = availableProjects.find(p => p.id === projectId);
    if (!project) return;

    // Check if already added
    if (memberSpaces.some(s => s.name === project.name)) {
      toast.error('Member already has access to this space');
      return;
    }

    // Add to local state
    setMemberSpaces([
      ...memberSpaces,
      {
        name: project.name,
        role: 'Member',
        projectId: project.id
      }
    ]);

    // Close the dialog
    setAddSpaceDialogOpen(false);
  };

  const handleRemoveSpace = spaceName => {
    setMemberSpaces(memberSpaces.filter(s => s.name !== spaceName));
  };

  const handleChangeSpaceRole = (spaceName, newRole) => {
    setMemberSpaces(
      memberSpaces.map(s =>
        s.name === spaceName ? { ...s, role: newRole } : s
      )
    );
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setAvatarFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadAvatar = async () => {
    if (!avatarFile || !member?.userId) return null;

    setUploadingAvatar(true);
    try {
      // Generate unique filename
      const fileExt = avatarFile.name.split('.').pop();
      const fileName = `avatar-${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

      // Upload to GCS
      const { publicUrl } = await gcsUploadFile(avatarFile, {
        folderPath: `trainingmatrix/images/${company.id}/${member.userId}`,
        fileName: fileName,
        contentType: avatarFile.type
      });

      return publicUrl;
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error('Failed to upload avatar');
      return null;
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Upload avatar if a new one was selected
      let avatarUrl = member.avatar_url;
      if (avatarFile) {
        const uploadedUrl = await uploadAvatar();
        if (uploadedUrl) {
          avatarUrl = uploadedUrl;
        }
      }

      // Update user profile (for both members with and without accounts)
      if (member.userId) {
        console.log('Updating profile with:', {
          first_name: firstName,
          last_name: lastName,
          email: email,
          member_userId: member.userId
        });

        // Use admin function to bypass RLS
        const { data, error: profileError } = await supabase
          .rpc('admin_update_user_profile', {
            target_user_id: member.userId,
            profile_updates: {
              first_name: firstName,
              last_name: lastName,
              display_name: `${firstName} ${lastName}`.trim(),
              email: email,
              job_title: jobTitle,
              location: location,
              bio: bio,
              avatar_url: avatarUrl
            }
          });

        console.log('Update result:', { data, error: profileError });

        if (profileError) throw profileError;

        // Update company role and corresponding permission group
        // Map roles to permission groups
        const roleToPermissionGroup = {
          'Admin': '545dcad2-4e3b-4eea-80ef-0293f5977bf2',    // Company Admin (all permissions)
          'Manager': 'e522b6f4-62c2-4a44-b369-001b826356af',  // Company Manager (limited permissions)
          'User': 'dcee0d55-d663-4ba2-91d2-05b9149bd2cc'     // Company User (basic permissions)
        };

        const { error: roleError } = await supabase
          .from('company_members')
          .update({
            role: companyRole,
            permission_group_id: roleToPermissionGroup[companyRole]
          })
          .eq('user_id', member.userId)
          .eq('company_id', company.id);

        if (roleError) throw roleError;
      }

      // Get current project memberships
      const { data: currentMemberships, error: fetchError } = await supabase
        .from('space_members')
        .select('project_id, projects!inner(name)')
        .eq('user_id', member.userId);

      if (fetchError) throw fetchError;

      // Determine which spaces to add/remove
      const currentSpaceNames =
        currentMemberships?.map(m => m.projects.name) || [];
      const newSpaceNames = memberSpaces.map(s => s.name);

      const spacesToRemove = currentSpaceNames.filter(
        name => !newSpaceNames.includes(name)
      );
      const spacesToAdd = memberSpaces.filter(
        s => !currentSpaceNames.includes(s.name)
      );
      const spacesToUpdate = memberSpaces.filter(s =>
        currentSpaceNames.includes(s.name)
      );

      // Remove memberships
      for (const spaceName of spacesToRemove) {
        const membership = currentMemberships.find(
          m => m.projects.name === spaceName
        );
        if (membership) {
          const { error } = await supabase
            .from('space_members')
            .delete()
            .eq('project_id', membership.project_id)
            .eq('user_id', member.userId);

          if (error) throw error;
        }
      }

      // Add new memberships
      for (const space of spacesToAdd) {
        // Find the project ID for this space
        const project = availableProjects.find(p => p.name === space.name);
        if (project) {
          const { error } = await supabase.from('space_members').insert({
            project_id: project.id,
            user_id: member.userId,
            role: space.role,
            created_date: new Date().toISOString()
          });

          if (error) throw error;
        }
      }

      // Update existing membership roles
      for (const space of spacesToUpdate) {
        const membership = currentMemberships.find(
          m => m.projects.name === space.name
        );
        if (membership) {
          const { error } = await supabase
            .from('space_members')
            .update({ role: space.role })
            .eq('project_id', membership.project_id)
            .eq('user_id', member.userId);

          if (error) throw error;
        }
      }

      toast.success('Member updated successfully');

      // If company role was changed, notify that user needs to re-login
      if (companyRole) {
        toast.info(`${firstName} ${lastName} will need to log out and back in to see their new ${companyRole} permissions`, {
          duration: 6000
        });
      }

      navigate('/company-team');
    } catch (error) {
      console.error('Error saving member:', error);
      toast.error('Failed to save member changes');
    } finally {
      setSaving(false);
    }
  };

  const getInitials = name => {
    return (
      name
        ?.split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2) || 'U'
    );
  };

  // Get projects not yet assigned to member
  const unassignedProjects = availableProjects.filter(
    project => !memberSpaces.some(space => space.name === project.name)
  );

  // Breadcrumb for navigation
  const parentBreadcrumb = {
    name: 'Company Users',
    onClick: () => navigate('/company-team')
  };

  // Header actions (save/cancel buttons)
  const headerActions = (
    <div className='flex items-center gap-2'>
      <Button
        variant='outline'
        size='sm'
        onClick={() => navigate('/company-team')}
        disabled={saving}
      >
        <ChevronLeft className='h-4 w-4 mr-1' />
        Cancel
      </Button>
      <Button
        size='sm'
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? (
          <>
            <Loader2 className='h-4 w-4 mr-1 animate-spin' />
            Saving...
          </>
        ) : (
          <>
            <Save className='h-4 w-4 mr-1' />
            Save Changes
          </>
        )}
      </Button>
    </div>
  );

  // Show loading while auth is being verified
  if (!authReady) {
    return (
      <AppShell3 pageTitle='' pageDescription=''>
        <ToolHeader
          spaceName='Company Users'
          toolName='Edit Member'
          currentTool='team'
          spaceTools={GLOBAL_TOOLS}
          isGlobalView={true}
          parentBreadcrumb={parentBreadcrumb}
        />
        <div className='flex items-center justify-center py-32'>
          <LoadingSpinner loading={true} size={20} />
        </div>
      </AppShell3>
    );
  }

  // Don't render anything if user doesn't have permission (redirect will happen via useEffect)
  if (!can('users.manage')) {
    return null;
  }

  if (loading) {
    return (
      <AppShell3 pageTitle='' pageDescription=''>
        <ToolHeader
          spaceName='Company Users'
          toolName='Edit Member'
          currentTool='team'
          spaceTools={GLOBAL_TOOLS}
          isGlobalView={true}
          parentBreadcrumb={parentBreadcrumb}
        />
        <div className='flex items-center justify-center py-32'>
          <LoadingSpinner loading={loading} size={20} />
        </div>
      </AppShell3>
    );
  }

  if (!member) {
    return (
      <AppShell3 pageTitle='' pageDescription=''>
        <ToolHeader
          spaceName='Company Users'
          toolName='Member Not Found'
          currentTool='team'
          spaceTools={GLOBAL_TOOLS}
          isGlobalView={true}
          parentBreadcrumb={parentBreadcrumb}
        />
        <div className='max-w-2xl mx-auto px-4 py-16'>
          <Card className='p-8 text-center'>
            <AlertCircle className='h-12 w-12 text-gray-400 mx-auto mb-4' />
            <h2 className='text-xl font-semibold mb-2'>Member Not Found</h2>
            <p className='text-gray-600 mb-6'>
              The team member you're looking for doesn't exist or you don't have
              permission to edit them.
            </p>
            <Button onClick={() => navigate('/company-team')}>
              Back to Company Users
            </Button>
          </Card>
        </div>
      </AppShell3>
    );
  }

  return (
    <AppShell3 pageTitle='' pageDescription=''>
      <ToolHeader
        spaceName='Company Users'
        toolName={member?.name || 'Member'}
        currentTool='team'
        spaceTools={GLOBAL_TOOLS}
        isGlobalView={true}
        parentBreadcrumb={parentBreadcrumb}
        actions={headerActions}
      />
      <PageTransition>
        <div className='max-w-6xl mx-auto px-4 lg:px-8 py-6'>
          <div className='flex gap-8'>
            {/* Left Sidebar - Navigation */}
            <div className='hidden lg:block w-56 flex-shrink-0'>
              <nav className='sticky top-24 space-y-1'>
                {SECTIONS.map((section) => {
                  const Icon = section.icon;
                  const isActive = activeSection === section.id;
                  return (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors text-left ${
                        isActive
                          ? 'bg-blue-50 text-blue-700 font-medium'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <Icon className={`h-4 w-4 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                      {section.label}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Right Content - Scrollable Sections */}
            <div className='flex-1 min-w-0 space-y-8'>
              {/* Profile Section */}
              <section id='profile' className='scroll-mt-24'>
                <Card>
                  <CardHeader>
                    <div className='flex items-center gap-4'>
                      <div className='relative'>
                        <Avatar className='h-20 w-20'>
                          <AvatarImage
                            src={avatarPreview || member?.avatar_url}
                            alt={member?.name}
                          />
                          <AvatarFallback className='text-xl'>
                            {getInitials(member?.name)}
                          </AvatarFallback>
                        </Avatar>
                        <label
                          htmlFor='avatar-upload'
                          className='absolute -bottom-1 -right-1 bg-primary text-primary-foreground rounded-full p-2 cursor-pointer hover:bg-primary/90 transition-colors shadow-lg'
                        >
                          <Camera className='h-4 w-4' />
                          <input
                            id='avatar-upload'
                            type='file'
                            accept='image/*'
                            onChange={handleAvatarChange}
                            className='hidden'
                            disabled={uploadingAvatar || saving}
                          />
                        </label>
                        {uploadingAvatar && (
                          <div className='absolute inset-0 bg-black/50 rounded-full flex items-center justify-center'>
                            <Loader2 className='h-6 w-6 text-white animate-spin' />
                          </div>
                        )}
                      </div>
                      <div className='flex-1'>
                        <CardTitle className='text-2xl'>{member?.name}</CardTitle>
                        <CardDescription className='text-base mt-1'>
                          {member?.email}
                        </CardDescription>
                        <div className='flex items-center gap-2 mt-2'>
                          {avatarFile && (
                            <Badge variant='outline'>
                              <Upload className='h-3 w-3 mr-1' />
                              New photo selected
                            </Badge>
                          )}
                          {member.hasAccount ? (
                            <Badge variant='secondary'>Active Account</Badge>
                          ) : (
                            <Badge variant='outline'>Invited</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              </section>

              {/* Personal Information Section */}
              <section id='personal' className='scroll-mt-24'>
                <Card>
                  <CardHeader>
                    <CardTitle>Personal Information</CardTitle>
                    <CardDescription>
                      Update basic profile information
                    </CardDescription>
                  </CardHeader>
                  <CardContent className='space-y-4'>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                      <div>
                        <Label htmlFor='firstName'>First Name</Label>
                        <Input
                          id='firstName'
                          value={firstName}
                          onChange={e => setFirstName(e.target.value)}
                          placeholder='Enter first name'
                          className='mt-1.5'
                        />
                      </div>
                      <div>
                        <Label htmlFor='lastName'>Last Name</Label>
                        <Input
                          id='lastName'
                          value={lastName}
                          onChange={e => setLastName(e.target.value)}
                          placeholder='Enter last name'
                          className='mt-1.5'
                        />
                      </div>
                      <div>
                        <Label htmlFor='email'>Email</Label>
                        <Input
                          id='email'
                          type='email'
                          value={email}
                          onChange={e => setEmail(e.target.value)}
                          placeholder='email@example.com'
                          className='mt-1.5'
                        />
                      </div>
                      <div>
                        <Label htmlFor='jobTitle'>Job Title</Label>
                        <Input
                          id='jobTitle'
                          value={jobTitle}
                          onChange={e => setJobTitle(e.target.value)}
                          placeholder='Enter job title'
                          className='mt-1.5'
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor='location'>Location</Label>
                      <Input
                        id='location'
                        value={location}
                        onChange={e => setLocation(e.target.value)}
                        placeholder='Enter location'
                        className='mt-1.5'
                      />
                    </div>
                    <div>
                      <Label htmlFor='bio'>Bio</Label>
                      <Textarea
                        id='bio'
                        value={bio}
                        onChange={e => setBio(e.target.value)}
                        placeholder='Enter bio...'
                        rows={3}
                        className='mt-1.5'
                      />
                    </div>
                  </CardContent>
                </Card>
              </section>

              {/* Company Role Section */}
              <section id='company-role' className='scroll-mt-24'>
                <Card>
                  <CardHeader>
                    <CardTitle>Company Role</CardTitle>
                    <CardDescription>
                      Set the member's role and permissions within the organization
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div>
                      <Label htmlFor='companyRole'>Role</Label>
                      <Select value={companyRole} onValueChange={setCompanyRole}>
                        <SelectTrigger className='mt-1.5'>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='Admin'>
                            <div className='flex items-center'>
                              <Shield className='inline h-3 w-3 mr-2 text-red-500' />
                              Admin - Full company access
                            </div>
                          </SelectItem>
                          <SelectItem value='Manager'>
                            <div className='flex items-center'>
                              <Shield className='inline h-3 w-3 mr-2 text-blue-500' />
                              Manager - Manage assigned spaces
                            </div>
                          </SelectItem>
                          <SelectItem value='User'>
                            <div className='flex items-center'>
                              <User className='inline h-3 w-3 mr-2 text-gray-500' />
                              User - Basic access
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <p className='text-xs text-gray-500 mt-2'>
                        Company role determines overall permissions across the organization.
                        The user will need to log out and back in to see their new permissions.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </section>

              {/* Kiosk PIN Section */}
              <section id='kiosk-pin' className='scroll-mt-24'>
                <KioskPinManager
                  userId={memberId}
                  currentPin={member?.kiosk_pin}
                  companyId={company?.id}
                  onPinChange={(newPin) => {
                    setMember(prev => prev ? { ...prev, kiosk_pin: newPin } : prev);
                  }}
                  isAdmin
                />
              </section>

              {/* Role Templates Section */}
              <section id='role-templates' className='scroll-mt-24'>
                <Card>
                  <CardHeader>
                    <CardTitle>Role Templates</CardTitle>
                    <CardDescription>
                      Roles applied to this member via the training matrix
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {memberRoleTemplates.length === 0 ? (
                      <div className='text-center py-8 border-2 border-dashed rounded-lg'>
                        <ShieldCheck className='h-10 w-10 text-gray-300 mx-auto mb-2' />
                        <p className='text-sm text-gray-500'>
                          No role templates applied yet
                        </p>
                        <p className='text-xs text-gray-400 mt-1'>
                          Apply roles from the member's profile qualifications tab
                        </p>
                      </div>
                    ) : (
                      <div className='space-y-3'>
                        {memberRoleTemplates.map(tmpl => (
                          <div
                            key={tmpl.id}
                            className='flex items-start gap-3 p-4 border rounded-lg bg-gray-50/50'
                          >
                            <div
                              className='w-3 h-3 rounded-full shrink-0 mt-1'
                              style={{ backgroundColor: tmpl.color || '#6366f1' }}
                            />
                            <div className='flex-1 min-w-0'>
                              <div className='flex items-center gap-2'>
                                <span className='font-medium text-sm'>{tmpl.name}</span>
                                <Badge
                                  variant='outline'
                                  className='text-[10px] py-0 px-1.5'
                                  style={{
                                    backgroundColor: (tmpl.color || '#6366f1') + '15',
                                    borderColor: (tmpl.color || '#6366f1') + '40',
                                    color: tmpl.color || '#6366f1'
                                  }}
                                >
                                  {tmpl.name}
                                </Badge>
                              </div>
                              {tmpl.description && (
                                <p className='text-xs text-muted-foreground mt-0.5'>{tmpl.description}</p>
                              )}
                              {tmpl.job_profile && (
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <button className='text-xs text-blue-600 hover:text-blue-800 mt-1.5 transition-colors'>
                                      View Job Profile
                                    </button>
                                  </PopoverTrigger>
                                  <PopoverContent className='w-96 max-h-72 overflow-y-auto' align='start'>
                                    <div className='space-y-2'>
                                      <div className='font-medium text-sm'>{tmpl.name} — Job Profile</div>
                                      <div
                                        className='prose prose-sm text-xs text-muted-foreground'
                                        dangerouslySetInnerHTML={{ __html: tmpl.job_profile }}
                                      />
                                    </div>
                                  </PopoverContent>
                                </Popover>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </section>

              {/* Space Access Section */}
              <section id='space-access' className='scroll-mt-24 pb-8'>
                <Card>
                  <CardHeader>
                    <div className='flex items-center justify-between'>
                      <div>
                        <CardTitle>Space Access</CardTitle>
                        <CardDescription>
                          Manage which spaces this member can access
                        </CardDescription>
                      </div>
                      {unassignedProjects.length > 0 && (
                        <Button
                          size='sm'
                          onClick={() => setAddSpaceDialogOpen(true)}
                        >
                          <Plus className='h-4 w-4 mr-1' />
                          Add Space
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Current Spaces */}
                    <div className='space-y-3'>
                      {memberSpaces.length === 0 ? (
                        <div className='text-center py-8 border-2 border-dashed rounded-lg'>
                          <Building className='h-10 w-10 text-gray-300 mx-auto mb-2' />
                          <p className='text-sm text-gray-500'>
                            No spaces assigned yet
                          </p>
                          {unassignedProjects.length > 0 && (
                            <Button
                              variant='outline'
                              size='sm'
                              className='mt-3'
                              onClick={() => setAddSpaceDialogOpen(true)}
                            >
                              <Plus className='h-4 w-4 mr-1' />
                              Add to a Space
                            </Button>
                          )}
                        </div>
                      ) : (
                        memberSpaces.map((space, idx) => (
                          <div
                            key={idx}
                            className='flex items-center justify-between p-4 border rounded-lg bg-gray-50/50 hover:bg-gray-100/50 transition-colors'
                          >
                            <div className='flex items-center gap-3 flex-1'>
                              <Badge variant='outline' className='text-sm px-3 py-1'>
                                {space.name}
                              </Badge>
                              <Select
                                value={space.role}
                                onValueChange={value =>
                                  handleChangeSpaceRole(space.name, value)
                                }
                              >
                                <SelectTrigger className='w-32'>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value='Owner'>
                                    <div className='flex items-center'>
                                      <Shield className='inline h-3 w-3 mr-2 text-red-500' />
                                      Owner
                                    </div>
                                  </SelectItem>
                                  <SelectItem value='Member'>
                                    <div className='flex items-center'>
                                      <User className='inline h-3 w-3 mr-2 text-blue-500' />
                                      Member
                                    </div>
                                  </SelectItem>
                                  <SelectItem value='Viewer'>
                                    <div className='flex items-center'>
                                      <User className='inline h-3 w-3 mr-2 text-gray-500' />
                                      Viewer
                                    </div>
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <Button
                              variant='ghost'
                              size='sm'
                              onClick={() => handleRemoveSpace(space.name)}
                              className='hover:bg-red-100 hover:text-red-600'
                            >
                              <X className='h-4 w-4' />
                            </Button>
                          </div>
                        ))
                      )}
                    </div>

                    {unassignedProjects.length === 0 && memberSpaces.length > 0 && (
                      <p className='text-sm text-gray-500 text-center py-4'>
                        Member has access to all available spaces
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Add Space Dialog */}
                <Dialog open={addSpaceDialogOpen} onOpenChange={setAddSpaceDialogOpen}>
                  <DialogContent className='sm:max-w-md p-0'>
                    <DialogHeader className='px-4 pt-4 pb-2'>
                      <DialogTitle>Add to Space</DialogTitle>
                      <DialogDescription>
                        Search and select a space to give {member?.name} access to.
                      </DialogDescription>
                    </DialogHeader>
                    <Command className='border-t'>
                      <CommandInput placeholder='Search spaces...' />
                      <CommandList className='max-h-72'>
                        <CommandEmpty>No spaces found.</CommandEmpty>
                        <CommandGroup>
                          {unassignedProjects.map(project => (
                            <CommandItem
                              key={project.id}
                              value={project.name}
                              onSelect={() => handleAddSpace(project.id)}
                              className='cursor-pointer'
                            >
                              <Building className='h-4 w-4 mr-2 text-gray-500' />
                              {project.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </DialogContent>
                </Dialog>
              </section>
            </div>
          </div>
        </div>
      </PageTransition>
    </AppShell3>
  );
}

export default EditMemberPage;
